/*
 * MainFrame.js:    This is the main UI container that includes layout, states and controls.
 *                  This UI is a proof of concept as a single-page-app.
 *                  It is divided into 4 sections (divs) that are displayed based on the app's progress.
 *                  The transitions from one section to another is based on user interaction or API fetch state.
 */
import React, { useEffect, useState } from "react";

import Div1InputEmail from "./Div1InputEmail/Div1InputEmail";
import Div2Searching from "./Div2UserInfo/Div2Searching";
import Div2Error from "./Div2UserInfo/Div2Error";
import Div2UserFound from "./Div2UserInfo/Div2UserFound";
import Div3MfaSelection from "./Div3VerifyIdentity/Div3MfaSelection";
import Div4MessageArea from "./Div4MessageArea/Div4MessageArea";
import Div3aPassCodeInput from "./Div3VerifyIdentity/Div3aPassCodeInput";
import Div3ManagerSelection from "./Div3VerifyIdentity/Div3ManagerSelection";

import useSearchUser from "../hooks/useSearchUser";
import useMfaVerification from "../hooks/useMfaVerification";
import useEmailManager from "../hooks/useEmailManager";
import useValidateIdentity from "../hooks/useValidateIdentity";
import useAccountActions from "../hooks/useAccountActions";

import { useStatusMessage } from "../contexts/StatusMessageContext";

import "./MainFrame.css";

const MainFrame = () => {
  const { status, clear: clearStatus } = useStatusMessage();

  // States for MainFrame
  const [activeStep, setActiveStep] = useState(1);

  // States for Div1 (Input email)
  const [enteredEmail, setEnteredEmail] = useState("");

  // States for Div2 (User info and buttons)
  const [isDiv2Visible, setDiv2Visible] = useState(false);
  const [div2FetchStatus, setDiv2FetchStatus] = useState("");
  const [div2Title, setDiv2Title] = useState("");
  const [userInfo, setUserInfo] = useState({});
  const [div2Error, setDiv2Error] = useState("");
  const [div2ButtonLabel, setDiv2ButtonLabel] = useState("");

  // States for Div3 (Verify User prompt)
  const [isDiv3Visible, setDiv3Visible] = useState(false);

  // States for Div3 (MFA)
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [activeMfaData, setActiveMfaData] = useState({});
  const [numPasscodeDigits, setNumPasscodeDigits] = useState(6);
  const [offNetworkEnrollmentSuccess, setOffNetworkEnrollmentSuccess] = useState(false);

  // States for Div3 (Manager selection)
  const [workflow, setWorkflow] = useState(1);
  const [shouldFetchManager, setShouldFetchManager] = useState(false);
  const [selectedManager, setSelectedManager] = useState({});

  // Identity Validation
  const [identityStep, setIdentityStep] = useState(0);

  // Div4 visibility — derived from status context
  const isDiv4Visible = status.message !== "" || status.title !== "";

  // Custom hooks
  const mfa = useMfaVerification();

  useValidateIdentity(identityStep, setIdentityStep, userInfo);

  const { searchUser } = useSearchUser(
    setActiveStep,
    setDiv2Visible,
    setDiv2FetchStatus,
    setDiv2Title,
    setUserInfo,
    setDiv2Error
  );

  const { emailManager: emailManagerIdVerify } = useEmailManager(
    2,
    selectedManager,
    userInfo
  );
  const { emailManager: emailManager2ndEmail } = useEmailManager(
    3,
    selectedManager,
    userInfo
  );

  const accountActions = useAccountActions(userInfo, () => searchUser(enteredEmail));

  const isDirectPasscodeFactor = (factorType) =>
    factorType === "totp" ||
    factorType === "token:software:totp" ||
    factorType === "token:hardware" ||
    factorType === "hardware_token";

  const isPasscodeFactor = (factorType) =>
    isDirectPasscodeFactor(factorType) ||
    ["sms", "call", "email"].includes(factorType);

  useEffect(() => {
    if (userInfo && Object.keys(userInfo).length > 0) {
      if (userInfo?.okta?.secondEmail === "NO_EMAIL") {
        setDiv2ButtonLabel("Set Secondary email");
        setWorkflow(3);
      } else if (userInfo?.okta?.secondEmail?.includes("@")) {
        setDiv2ButtonLabel("Reset Password");
        setWorkflow(1);
      }
    }
  }, [userInfo]);

  useEffect(() => {
    if (selectedManager && Object.keys(selectedManager).length > 0) {
      if (workflow === 2) {
        emailManagerIdVerify();
      } else if (workflow === 3) {
        emailManager2ndEmail();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedManager]);

  useEffect(() => {
    if (mfa.phase === "awaiting_passcode" && isPasscodeFactor(activeMfaData.factorType)) {
      setShowCodeInput(true);
      setActiveStep(3);
    } else if (["success", "error", "idle"].includes(mfa.phase)) {
      setShowCodeInput(false);
    }
  }, [mfa.phase, activeMfaData]);

  const clearSearchDependentState = () => {
    setActiveStep(1);
    setDiv2Visible(false);
    setDiv2FetchStatus("");
    setDiv2Title("");
    setUserInfo({});
    setDiv2Error("");
    setDiv3Visible(false);
    setShowCodeInput(false);
    setActiveMfaData({});
    setWorkflow(1);
    setShouldFetchManager(false);
    setSelectedManager({});
    setOffNetworkEnrollmentSuccess(false);
    setIdentityStep(0);
    mfa.reset();
    clearStatus();
  };

  const resetState = () => {
    setActiveStep(1);
    setEnteredEmail("");

    setDiv2Visible(false);
    setDiv2FetchStatus("");
    setDiv2Title("");
    setUserInfo({});
    setDiv2Error("");

    setDiv3Visible(false);
    setShowCodeInput(false);
    setWorkflow(1);

    setSelectedManager({});
    setOffNetworkEnrollmentSuccess(false);

    setIdentityStep(0);

    mfa.reset();
    clearStatus();
  };

  /*
   *******************************************************************************************************************
   * Div1: Input Email
   *******************************************************************************************************************
   */
  const Div1SearchButtonClicked = async () => {
    setDiv3Visible(false);
    clearStatus();
    setShowCodeInput(false);
    setActiveMfaData({});
    setShouldFetchManager(false);
    setSelectedManager({});
    setOffNetworkEnrollmentSuccess(false);
    setIdentityStep(0);
    mfa.reset();

    await searchUser(enteredEmail);
  };

  /*
   *******************************************************************************************************************
   * Div2: Display user info, or error if not found
   *******************************************************************************************************************
   */

  const handleValidateIdButtonPress = async () => {
    setDiv3Visible(false);
    clearStatus();
    setShowCodeInput(false);

    setActiveStep(4);
    setIdentityStep(1);
  };

  const handleDiv2ButtonPress = () => {
    setDiv3Visible(true);
    clearStatus();
    setActiveStep(3);

    if (div2ButtonLabel === "Reset Password") {
      Div2ResetPasswordClicked();
    } else {
      Div2Set2ndEmailClicked();
    }
  };

  const handleEmailResetButtonPress = async () => {
    await accountActions.resetSecondaryEmail();
  };

  const handleUnlockAccountButtonPress = async () => {
    await accountActions.unlockAccount();
  };

  const handleAddToMfaPilotButtonPress = async () => {
    setActiveStep(4);
    await accountActions.addToMfaPilot();
  };

  const handleOffNetworkEnrollmentButtonPress = async () => {
    setOffNetworkEnrollmentSuccess(false);
    const succeeded = await accountActions.enableOffNetworkEnrollment();
    setOffNetworkEnrollmentSuccess(succeeded);
  };

  const Div2ResetPasswordClicked = () => {
    setShowCodeInput(false);
    mfa.reset();
    setWorkflow(1);
  };

  const Div2Set2ndEmailClicked = () => {
    setShouldFetchManager(true);
    setWorkflow(3);
  };

  const Div2StartOverClicked = () => {
    resetState();
  };

  /*
    *******************************************************************************************************************
    * Div3: One of the 3 cases:
    *      Workflow 1. Use to identify themselves by MFA
    *      Workflow 2. Manager to verify user's identity
    *      Workflow 3. Manager to add user's secondary email
    *******************************************************************************************************************
   */
  const onSelectMfaFactor = (selectedFactorId) => {
    if (mfa.phase !== "idle") {
      return;
    }

    setWorkflow(1);

    const selectedMfaObject = (userInfo.factors ?? []).find(
      (item) => item.id === selectedFactorId
    );

    if (!selectedMfaObject) {
      return;
    }

    const factor = {
      userFirstName: userInfo.firstName,
      userEmail: userInfo.email,
      userId: userInfo.okta.id,
      factorId: selectedMfaObject.id,
      factorType: selectedMfaObject.factorType,
      factorName: selectedMfaObject.name,
    };

    setActiveMfaData(factor);

    if (isPasscodeFactor(selectedMfaObject.factorType)) {
      setNumPasscodeDigits(6);
      clearStatus();
      setShowCodeInput(isDirectPasscodeFactor(selectedMfaObject.factorType));
      setActiveStep(isDirectPasscodeFactor(selectedMfaObject.factorType) ? 3 : 4);
      mfa.start(factor);
    } else {
      setShowCodeInput(false);
      clearStatus();
      setActiveStep(4);
      mfa.start(factor);
    }
  };

  const onCodeEntered = async (code) => {
    setActiveStep(4);
    await mfa.submitPasscode(code);
  };

  const Div3UseManagerVerifyClicked = async () => {
    setDiv3Visible(true);
    setShowCodeInput(false);
    clearStatus();
    setWorkflow(2);
    setShouldFetchManager(true);
  };

  const onSelectManager = async (manager) => {
    setSelectedManager(manager);
    setActiveStep(4);
  };

  const Div4OkClicked = () => {
    resetState();
  };

  return (
    <div className="main-wrapper">
      <Div1InputEmail
        enteredEmail={enteredEmail}
        setEnteredEmail={setEnteredEmail}
        Div1SearchButtonClicked={Div1SearchButtonClicked}
        setUserInfo={setUserInfo}
        setDiv2Visible={setDiv2Visible}
        onEditAfterSearch={clearSearchDependentState}
        activeStep={activeStep}
      />

      {isDiv2Visible && div2FetchStatus === "searching" && (
        <Div2Searching div2Title={div2Title} activeStep={activeStep} />
      )}

      {isDiv2Visible && div2FetchStatus === "data" && (
        <Div2UserFound
          div2Title={div2Title}
          userInfo={userInfo}
          activeStep={activeStep}
          div2ButtonLabel={div2ButtonLabel}
          handleDiv2ButtonPress={handleDiv2ButtonPress}
          handleValidateIdButtonPress={handleValidateIdButtonPress}
          handleEmailResetButtonPress={handleEmailResetButtonPress}
          handleUnlockAccountButtonPress={handleUnlockAccountButtonPress}
          handleAddToMfaPilotButtonPress={handleAddToMfaPilotButtonPress}
          handleOffNetworkEnrollmentButtonPress={handleOffNetworkEnrollmentButtonPress}
          offNetworkEnrollmentSuccess={offNetworkEnrollmentSuccess}
        />
      )}

      {isDiv2Visible && div2FetchStatus === "error" && (
        <Div2Error
          div2Title={div2Title}
          errorMsg={div2Error}
          Div2StartOverClicked={Div2StartOverClicked}
          activeStep={activeStep}
        />
      )}

      {isDiv3Visible && workflow === 1 && (
        <Div3MfaSelection
          mfaData={userInfo.factors ?? []}
          onSelectMfa={onSelectMfaFactor}
          activeStep={activeStep}
          Div3UseManagerVerifyClicked={Div3UseManagerVerifyClicked}
          isMfaLocked={mfa.phase !== "idle"}
        />
      )}

      {showCodeInput && (
        <Div3aPassCodeInput
          onCodeEntered={onCodeEntered}
          activeStep={activeStep}
          numDigits={numPasscodeDigits}
        />
      )}

      {isDiv3Visible && workflow === 2 && shouldFetchManager && (
        <Div3ManagerSelection
          shouldFetchManager={shouldFetchManager}
          enteredEmail={enteredEmail}
          activeStep={activeStep}
          onSelectManager={(manager) => onSelectManager(manager, 2)}
          title="Manager Must Verify Identity"
          buttonLabel="Send Identity Verification"
          action="Verifying identity"
        />
      )}

      {isDiv3Visible && workflow === 3 && shouldFetchManager && (
        <Div3ManagerSelection
          shouldFetchManager={shouldFetchManager}
          enteredEmail={enteredEmail}
          activeStep={activeStep}
          onSelectManager={(manager) => onSelectManager(manager, 3)}
          title="Manager Must Set Secondary Email"
          buttonLabel="Send Notification Email"
          action="Adding Secondary email"
        />
      )}

      {isDiv4Visible && (
        <Div4MessageArea
          activeStep={activeStep}
          onOk={Div4OkClicked}
        />
      )}
    </div>
  );
};

export default MainFrame;
